'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Env from 'src/environmentVariables/EnvironmentVariables';
import usePricingSourceStore from 'src/stores/pricing/usePricingSourceStore';
import { VelocityWindow } from 'src/window/velocityWindow';
import { dlog } from 'src/dev';
import { publishPricingWsBatch } from 'src/stores/marketLiveState/adapters/pricingWebsocketAdapter';
import {
	PricingWebsocketClient,
	ProcessedPricingData,
} from './PricingWebsocketClient';
import { usePricingDataPipeline } from './hooks/usePricingDataPipeline';
import { usePricingTabVisibilityAndRecovery } from './hooks/usePricingTabVisibilityAndRecovery';

export const PricingSubscriptionProvider = ({
	children,
}: {
	children?: ReactNode;
}) => {
	const clientRef = useRef<PricingWebsocketClient | null>(null);
	const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
	const lastFallbackTimestamp = useRef<number>(0);
	const [subscriptionRestartKey, setSubscriptionRestartKey] = useState(0);

	const setSource = usePricingSourceStore((s) => s.setSource);

	// Data pipeline hook
	const { createDataPipeline, destroyCurrentPipeline$ } =
		usePricingDataPipeline({
			lastFallbackTimestamp,
			subscriptionRestartKey,
		});

	const updatePricingCache = useCallback(
		(pricingData: ProcessedPricingData[]) => {
			// The live-state module is the authoritative oracle/mark surface;
			// pricing-ws data is published there and nowhere else.
			publishPricingWsBatch(pricingData);
		},
		[]
	);

	const destroyClient = useCallback(() => {
		if (clientRef.current) {
			clientRef.current.destroy();
			clientRef.current = null;
		}
	}, []);

	const createClient = useCallback(() => {
		if (clientRef.current) {
			return clientRef.current;
		}

		const wsUrl = Env.dataApiWsUrl;

		const client = new PricingWebsocketClient({
			websocketUrl: wsUrl,
			onConnectionStateChange: (connected: boolean) => {
				if (connected) {
					setSource('websocket');
				} else {
					lastFallbackTimestamp.current = Date.now();
					setSource('polling');
				}
			},
			onError: () => {
				lastFallbackTimestamp.current = Date.now();
				setSource('polling');
			},
		});

		clientRef.current = client;
		return client;
	}, [setSource]);

	const subscribeWithPipeline = useCallback(
		(client: PricingWebsocketClient) => {
			// Cleanup previous subscription
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}

			const subscription = createDataPipeline(client).subscribe({
				next: updatePricingCache,
				error: (err) => {
					dlog('pricing_websocket_subscription', 'Pipeline error', {
						error: err,
					});
				},
			});

			subscriptionRef.current = subscription;
		},
		[createDataPipeline, updatePricingCache]
	);

	const performRecovery = useCallback(() => {
		// Signal pipeline destruction (don't complete - let the hook's cleanup handle that)
		destroyCurrentPipeline$.next();

		// Cleanup
		if (subscriptionRef.current) {
			subscriptionRef.current.unsubscribe();
			subscriptionRef.current = null;
		}
		destroyClient();

		// Increment restart key to force new pipeline (this triggers new Subject creation)
		setSubscriptionRestartKey((prev) => prev + 1);
	}, [destroyCurrentPipeline$, destroyClient]);

	const onTabInactiveCleanup = useCallback(() => {
		// Signal pipeline destruction
		destroyCurrentPipeline$.next();
		if (subscriptionRef.current) {
			subscriptionRef.current.unsubscribe();
			subscriptionRef.current = null;
		}
		destroyClient();
		// Don't increment subscriptionRestartKey here — it would trigger
		// the main useEffect and immediately reconnect. Recovery will
		// happen via performRecovery when the tab becomes active again.
	}, [destroyCurrentPipeline$, destroyClient]);

	// Tab visibility and recovery hook
	usePricingTabVisibilityAndRecovery({
		lastFallbackTimestamp,
		performRecovery,
		onTabInactiveCleanup,
	});

	// Main subscription effect
	useEffect(() => {
		const client = createClient();
		client.subscribe();
		subscribeWithPipeline(client);

		return () => {
			// Signal pipeline destruction (hook's cleanup handles complete())
			destroyCurrentPipeline$.next();
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			destroyClient();
		};
	}, [
		createClient,
		destroyClient,
		subscribeWithPipeline,
		destroyCurrentPipeline$,
		subscriptionRestartKey,
	]);

	// Dev event listener for simulating websocket failure
	useEffect(() => {
		const handleSimulatePricingWsFailure = () => {
			// Signal pipeline destruction
			destroyCurrentPipeline$.next();

			// Cleanup
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
			destroyClient();

			// Set fallback timestamp and switch to polling
			lastFallbackTimestamp.current = Date.now();
			setSource('polling');

			// Increment restart key to properly complete the Subject via hook cleanup
			setSubscriptionRestartKey((prev) => prev + 1);
		};

		VelocityWindow.devEventEmitter.on(
			'simulate_pricing_ws_failure',
			handleSimulatePricingWsFailure
		);

		return () => {
			VelocityWindow.devEventEmitter.off(
				'simulate_pricing_ws_failure',
				handleSimulatePricingWsFailure
			);
		};
	}, [destroyCurrentPipeline$, destroyClient, setSource]);

	return <>{children}</>;
};
